import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import { fmtMDT } from "../lib/time";
import { useToast } from "../hooks/use-toast";
import {
  Globe, Wrench, RefreshCw, FileText, FileDown, ChevronRight,
} from "lucide-react";

const SEV_STYLE = {
  critical: "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300",
  high:     "border-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-300",
  medium:   "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  low:      "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  info:     "border-slate-500/40 bg-slate-500/10 text-slate-700 dark:text-slate-300",
};

const SEV_ORDER = ["critical", "high", "medium", "low", "info"];

export default function FindingsDrilldown({
  open, onClose,
  title, description,
  filter, // { severity?: 'critical' | 'critical,high' | ..., target?: string, tool?: string }
  onOpenScan, // (scan_id) => void
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sevFilter, setSevFilter] = useState(null); // client-side chip filter
  const [exporting, setExporting] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter?.severity) params.set("severity", filter.severity);
      if (filter?.target)   params.set("target",   filter.target);
      if (filter?.tool)     params.set("tool",     filter.tool);
      params.set("limit", "500");
      const { data } = await api.get(`/vulnerability/findings?${params.toString()}`);
      setData(data);
    } catch {
      setData({ findings: [], total: 0 });
    } finally { setLoading(false); }
  }, [open, filter]);

  useEffect(() => {
    if (open) { setSevFilter(null); load(); }
  }, [open, load]);

  const findings = (data?.findings || []).filter((f) => {
    if (!sevFilter) return true;
    return (f.severity || "").toLowerCase() === sevFilter;
  });

  // Build a per-severity count from the loaded findings (client-side for chip toggles).
  const sevCounts = SEV_ORDER.reduce((acc, k) => ({ ...acc, [k]: 0 }), {});
  (data?.findings || []).forEach((f) => {
    const s = (f.severity || "info").toLowerCase();
    if (sevCounts[s] != null) sevCounts[s] += 1;
  });

  const exportPdf = useCallback(async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filter?.severity) params.set("severity", filter.severity);
      if (filter?.target)   params.set("target",   filter.target);
      if (filter?.tool)     params.set("tool",     filter.tool);
      const { data: html } = await api.get(`/vulnerability/findings/report?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast({ title: "Export failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  }, [filter, toast]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0" data-testid="findings-drilldown-modal">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <div className="flex items-center justify-between gap-3 pr-6">
            <DialogTitle className="flex items-center gap-2 min-w-0">
              <FileText className="w-4 h-4 text-primary shrink-0" />
              <span className="truncate">{title || "Findings"}</span>
              {data && (
                <span className="text-[10.5px] rounded-full border border-border bg-muted/50 text-muted-foreground px-1.5 py-0.5 font-semibold shrink-0">
                  {data.total}
                </span>
              )}
            </DialogTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={exportPdf}
              disabled={exporting}
              className="gap-1.5 shrink-0"
              data-testid="findings-export-pdf-btn"
            >
              <FileDown className={cn("w-3.5 h-3.5", exporting && "animate-pulse")} />
              {exporting ? "Exporting…" : "Export to PDF"}
            </Button>
          </div>
          {description && <DialogDescription>{description}</DialogDescription>}
          {/* Client-side severity chips let the user narrow further without another API call. */}
          {(data?.findings?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              <button
                type="button"
                onClick={() => setSevFilter(null)}
                className={cn("text-[10.5px] rounded-full border px-2 py-0.5 font-semibold uppercase tracking-wide",
                  !sevFilter ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted/50")}
                data-testid="findings-sev-all"
              >
                All · {data?.findings?.length ?? 0}
              </button>
              {SEV_ORDER.filter((k) => sevCounts[k] > 0).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setSevFilter(k === sevFilter ? null : k)}
                  data-testid={`findings-sev-${k}`}
                  className={cn(
                    "text-[10.5px] rounded-full border px-2 py-0.5 font-semibold uppercase tracking-wide capitalize",
                    sevFilter === k ? SEV_STYLE[k] : "border-border text-muted-foreground hover:bg-muted/50"
                  )}
                >
                  {k} · {sevCounts[k]}
                </button>
              ))}
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading && !data ? (
            <div className="space-y-2">
              {[...Array(6)].map((_, i) => <div key={i} className="h-14 rounded-md bg-muted/40 animate-pulse" />)}
            </div>
          ) : findings.length === 0 ? (
            <div className="py-10 text-center text-[13px] text-muted-foreground">
              No findings match this filter.
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid="findings-drilldown-list">
              {findings.map((f) => (
                <li key={f.id} className="rounded-lg border border-border bg-background/60 p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn(
                      "text-[10px] rounded-full border px-1.5 py-0.5 font-semibold uppercase tracking-wide",
                      SEV_STYLE[(f.severity || "info").toLowerCase()] || SEV_STYLE.info
                    )}>{f.severity || "info"}</span>
                    <span className="text-[10px] rounded border border-border bg-muted/50 px-1.5 py-0.5 font-mono inline-flex items-center gap-1">
                      <Wrench className="w-2.5 h-2.5" /> {f.tool}
                    </span>
                    <span className="text-[13px] font-semibold min-w-0 truncate">{f.name}</span>
                  </div>
                  {f.description && (
                    <div className="text-[12px] text-muted-foreground mt-1 line-clamp-3 whitespace-pre-wrap">
                      {f.description}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-[10.5px] text-muted-foreground mt-1.5 flex-wrap">
                    <span className="inline-flex items-center gap-1 font-mono">
                      <Globe className="w-3 h-3" /> {f.host || f.target}
                    </span>
                    {f.matched_at && <span className="font-mono truncate">· {f.matched_at}</span>}
                    <span>· {fmtMDT(f.discovered_at)}</span>
                    {onOpenScan && f.scan_id && (
                      <button
                        type="button"
                        onClick={() => onOpenScan(f.scan_id)}
                        className="ml-auto text-primary hover:underline inline-flex items-center gap-0.5"
                        data-testid={`open-scan-${f.scan_id}`}
                      >
                        Open scan <ChevronRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter className="px-5 py-3 border-t border-border">
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5 mr-auto" data-testid="findings-refresh-btn">
            <RefreshCw className={cn("w-3.5 h-3.5", loading && "animate-spin")} /> Refresh
          </Button>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
